import EventEmitter from './eventSubscribers';
import { LABEL } from '../veriables';

const UNKNOWN_DEVICE_STATE = 'UNKNOWN';
const RETRY_DELAY_MS = 500;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const formatReconnectMessage = (attempt, totalAttempts) =>
  LABEL().CARD_READER_RECONNECTING_ATTEMPT
    .replace('{attempt}', attempt)
    .replace('{total}', totalAttempts);

const requestCardReaderStatus = () =>
  new Promise((resolve) => {
    EventEmitter.isCardReaderReady(
      (message) => resolve({ message, error: null }),
      (error) => resolve({ message: null, error })
    );
  });

const reconnectCardReader = () =>
  new Promise((resolve) => {
    EventEmitter.reconnectToCardReader(
      (message) => resolve({ message, error: null }),
      (error) => resolve({ message: null, error })
    );
  });

const getDeviceState = (message, error) =>
  message?.deviceState ||
  error?.deviceState ||
  error?.lastObservedDeviceState ||
  error?.initialDeviceState ||
  UNKNOWN_DEVICE_STATE;

const buildSuccessResult = (message, attemptsUsed) => ({
  ok: true,
  ready: true,
  attemptsUsed,
  bypassed: Boolean(message?.bypassed),
  deviceState: getDeviceState(message, null),
  message,
});

const buildFailureResult = (error, attemptsUsed, initialDeviceState, lastObservedDeviceState) => ({
  ok: false,
  ready: false,
  attemptsUsed,
  deviceState: lastObservedDeviceState || UNKNOWN_DEVICE_STATE,
  initialDeviceState: initialDeviceState || UNKNOWN_DEVICE_STATE,
  lastObservedDeviceState: lastObservedDeviceState || UNKNOWN_DEVICE_STATE,
  error,
  errorMessage:
    error?.message || LABEL().CARD_READER_NOT_READY_GENERIC,
});

const ensureCardReaderConnection = async ({
  maxReconnectAttempts = 3,
  onProgress,
  skipConnectionCheck = false,
} = {}) => {
  if (skipConnectionCheck) {
    return buildSuccessResult(
      {
        ready: true,
        bypassed: true,
        deviceState: 'PAYMENTS_DISABLED',
      },
      0
    );
  }

  onProgress && onProgress(LABEL().CARD_READER_CHECKING_CONNECTION);

  const statusResult = await requestCardReaderStatus();

  if (statusResult.message?.ready) {
    return buildSuccessResult(statusResult.message, 0);
  }

  const initialDeviceState = getDeviceState(statusResult.message, statusResult.error);
  let lastObservedDeviceState = initialDeviceState;
  let lastError = statusResult.error || null;

  for (let attempt = 1; attempt <= maxReconnectAttempts; attempt += 1) {
    onProgress && onProgress(formatReconnectMessage(attempt, maxReconnectAttempts));

    const reconnectResult = await reconnectCardReader();
    lastObservedDeviceState = getDeviceState(reconnectResult.message, reconnectResult.error);
    lastError = reconnectResult.error || lastError;

    if (reconnectResult.message?.ready) {
      return buildSuccessResult(reconnectResult.message, attempt);
    }

    if (reconnectResult.error) {
      lastError = reconnectResult.error;
    }

    if (attempt < maxReconnectAttempts) {
      await wait(RETRY_DELAY_MS);
    }
  }

  return buildFailureResult(
    lastError,
    maxReconnectAttempts,
    initialDeviceState,
    lastObservedDeviceState
  );
};

export default ensureCardReaderConnection;
